import { Experiment } from '@amplitude/experiment-js-client';
import Cookies from 'universal-cookie';
import { anonymousIdWatcher } from './SegmentShared';
import { getAnonymousId } from './SegmentShared';
import { isFoundingMember } from 'Utils/Plans';
import { segmentTrackEvent } from 'Utils/Analytics';
import { getDefaultConfig } from 'Utils/Store';
import { MONTH_UPGRADE_SKU, MONTH_UPGRADE_SKU_VARIANT } from 'Utils/Constants';
import { getDppSkus } from 'Api/DppApi';

const MLP_COOKIE = 'nation_mlp_cmp_var';

const cookies = new Cookies();

let amplitudeInstance;

export const launchAmplitude = async () => {
  const { amplitudeKey } = getDefaultConfig();

  // (1) Initialize the experiment client
  amplitudeInstance = Experiment.initialize(amplitudeKey);

  await anonymousIdWatcher();

  await resetAmplitudeUser();
};

export const resetAmplitudeUser = async () => {
  //only reset if id changed:
  const newUserDeviceId = getAnonymousId();
  if (getAmplitudeUser().device_id !== newUserDeviceId) {
    setInitUserAttributes();

    const user = getAmplitudeUser();

    resetExposureHistory();

    // (2) Fetch experiment variants for a user
    await fetchAndCollectExperiments(user);
  }
};

/**
 *
 *  EXPERIMENTS:
 * **/

//list of experiment keys:
const SET_DEFAULT_PLAN_KEY = 'set-default-plan';
const DEFAULT_PLAN_FLOW_KEY = 'default-plan-flow';
const PLAN_EXP_FN1YPA47_KEY = 'yearly-discount-33';
const OCT_NAT_AA = 'october-nation-aa';
const DEV_DUMMY_TEST = 'dev-dummy-test-dec-2022';
const TWO_PLANS_FUNNEL_PLACEMENT = 'two-plans-funnel-placement';
const SKU_PRICE_COMBOS = 'sku-price-combinations';
const MLP_PRICE_STRIKETHROUGH = 'mlp-price-strikethrough';
const MONTHLY_30_DAY_FREE_TRIAL = 'monthly-30-day-free-trial';
const MLP_COUNTDOWN_CLOCK = 'mlp-countdown-clock';
const OCT_2023_PRICE_TEST = 'october-2023-price-test';
const WEB_ANON_CTA = 'web-anon-cta';
const MLP_WEB_BROWSE = 'mlp-web-browse-content';
const GET_STARTED_JOIN_NOW = 'get-started-vs-join-now';
const MLP_BF_TIMER = 'mlp-bf2023-timer';
const JAN_24_PRICE_TEST = 'january-2024-price-test';
const APR_24_PRICE_TEST = 'web-price-test-april-2024';
const MAY_24_PRICE_TEST = 'web-may-price-test-2024';
const JUNE_24_PRICE_TEST = 'web-june-price-test-2024';
const APR_25_PRICE_TEST = 'web-april-price-test-2025';

//list of variant keys:
const MONTHLY_BAU_YEARLY_LOW = 'monthly-bau-yearly-low';
const MONTHLY_HIGH_YEARLY_LOW = 'monthly-high-yearly-low';
const MONTHLY_YEARLY_HIGH = 'monthly-yearly-high';

//all potentially used experiment keys should be listed here.
const EXPERIMENT_KEYS = [
  MLP_PRICE_STRIKETHROUGH,
  MONTHLY_30_DAY_FREE_TRIAL,
  MLP_COUNTDOWN_CLOCK,
  OCT_2023_PRICE_TEST,
  WEB_ANON_CTA,
  MLP_WEB_BROWSE,
  GET_STARTED_JOIN_NOW,
  MLP_BF_TIMER,
  SET_DEFAULT_PLAN_KEY,
  JAN_24_PRICE_TEST,
  APR_24_PRICE_TEST,
  MAY_24_PRICE_TEST,
  JUNE_24_PRICE_TEST,
  APR_25_PRICE_TEST,
];

//TBD: Would it be better to put this in store?
// { [experimentKey]: { value: 'variantValue', payload: 'variantPayload' } }
let experimentsDataObj = {};

//Look up a flag's variant
//obj with keys of value and (optional) payload, e.g. {value: 'treatment'}
export const getVariant = (experimentFlagKey) => amplitudeInstance.variant(experimentFlagKey);

const fetchExperiments = async (user) => await amplitudeInstance.fetch(user, { fetchTimeoutMillis: 3000 });

const resetExperimentsData = () => {
  experimentsDataObj = {};
};

const checkIfExperimentIsActive = (experimentKey) =>
  experimentsDataObj?.[experimentKey]?.value !== undefined && experimentsDataObj?.[experimentKey]?.value !== 'false';
//Generate campaign string for Segment from experiments object
export const getSegmentAmplitudeString = () => {
  if (experimentsDataObj) {
    const segmentString = Object.entries(experimentsDataObj).map(
      ([experimentKey, variant]) => `${experimentKey.trim()}:${variant.value.trim()}`
    );
    return segmentString.join(',');
  }
  return '';
};

//Set string of campaigns from Amplitude for eVar65 on the utag for Tealium and use in Segment traits
export const collectAmplitudeExperiments = () => {
  EXPERIMENT_KEYS.forEach((key) => {
    const variant = getVariant(key);
    if (variant.value) {
      experimentsDataObj[key] = variant;
    }
  });
  const experimentsString = getSegmentAmplitudeString();

  window.utag_data = window.utag_data || {};
  if (experimentsString.length > 0) {
    window.utag_data.user = {
      testname: experimentsString,
    };
  } else {
    window.utag_data.user = {
      testname: undefined,
    };
  }
};

const fetchAndCollectExperiments = async (user) => {
  await fetchExperiments(user);
  resetExperimentsData();
  collectAmplitudeExperiments();
};

/**
 *
 *  EXPOSURE EVENTS:
 * **/

/*
  We only want to fire 1 exposure per experiment per user per session
  structure is { [experimentKey]: boolean }  true if fired
*/
let firedExposures = {};

const resetExposureHistory = () => {
  firedExposures = {};
};

/**
 * Exposure Event Helper
 * Per Amplitude documentation, make a segment event called '$exposure'
 * Tells segment and amplitude that user was exposed to an experiment
 * **/
const exposureEvent = (experimentKey) => {
  if (experimentsDataObj[experimentKey] && !firedExposures[experimentKey]) {
    segmentTrackEvent('$exposure', {
      flag_key: experimentKey,
      variant: experimentsDataObj[experimentKey].value,
      experiment_key: experimentsDataObj[experimentKey].expKey,
    });

    firedExposures[experimentKey] = true;
  }
};

//Helpers for specific experiments:

// web-june-price-test-2024 experiment:
export const getJunePriceTestVariant = () => experimentsDataObj[JUNE_24_PRICE_TEST]?.value;

export const getJunePriceTestSkus = async (dppPlans) => {
  let plans = dppPlans;
  try {
    // 'control', 'variant-1' or undefined
    const variant = getJunePriceTestVariant();
    if (variant === 'control') {
      plans = await getDppSkus(['FOXNATION1M-WEB15', 'FOXNATION12M-WEB13']);
    } else if (variant === 'variant-1') {
      plans = await getDppSkus(['FOXNATION1M-WEB16', 'FOXNATION12M-WEB14']);
    }
  } finally {
    return [...plans, ...dppPlans.filter((i) => i.planId === 'FOXNATION24M-WEB')];
  }
};

// web-april-price-test-2025 experiment:
export const getApr2025PriceTestVariant = () => experimentsDataObj[APR_25_PRICE_TEST]?.value;

export const getApr2025PriceTestSkus = async (dppPlans) => {
  let plans = dppPlans;
  try {
    // 'control', 'variant-1', 'variant-2' or undefined
    const variant = getApr2025PriceTestVariant();
    if (variant === 'control') {
      plans = await getDppSkus(['FN1MONTH799-WEB', 'FN1YEAR5949-WEB']);
    } else if (variant === 'variant-1') {
      plans = await getDppSkus(['FN1MONTH899-WEB', 'FN1YEAR7149-WEB']);
    } else if (variant === 'variant-2') {
      plans = await getDppSkus(['FN1MONTH999-WEB', 'FN1YEAR8349-WEB']);
    }
  } finally {
    return plans;
  }
};

// web-price-test-april-2024 experiment:
export const getAprPriceTestVariant = () => experimentsDataObj[APR_24_PRICE_TEST]?.value;

export const getAprPriceTestSkus = async (dppPlans) => {
  let plans = dppPlans;
  try {
    // 'control' or 'variant' or undefined
    const variant = getAprPriceTestVariant();
    if (variant === 'control') {
      plans = await getDppSkus(['FOXNATION1M-WEB9', 'FOXNATION12M-WEB7']);
    } else if (variant === 'variant') {
      plans = await getDppSkus(['FOXNATION1M-WEB8', 'FOXNATION12M-WEB6']);
    }
  } finally {
    return plans;
  }
};

// web-may-price-test-2024 experiment:
export const getMayPriceTestVariant = () => experimentsDataObj[MAY_24_PRICE_TEST]?.value;

export const getMayPriceTestSkus = async (dppPlans) => {
  let plans = dppPlans;
  try {
    // 'control', 'variant-1', 'variant-2' or undefined
    const variant = getMayPriceTestVariant();
    if (variant === 'control') {
      plans = await getDppSkus(['FOXNATION1M-WEB13', 'FOXNATION12M-WEB11']);
    } else if (variant === 'variant-1') {
      plans = await getDppSkus(['FOXNATION1M-WEB11', 'FOXNATION12M-WEB9']);
    } else if (variant === 'variant-2') {
      plans = await getDppSkus(['FOXNATION1M-WEB12', 'FOXNATION12M-WEB10']);
    }
  } finally {
    return [...plans, ...dppPlans.filter((i) => i.planId === 'FOXNATION24M-WEB')];
  }
};

//january-2024-price-test experiment:

export const getJanPriceTestVariant = () => {
  // 'control' or 'variant' or undefined
  return experimentsDataObj[JAN_24_PRICE_TEST]?.value;
};

export const getJanPriceTestSkus = async (dppPlans) => {
  let plans = dppPlans;
  try {
    // 'control' or 'variant' or undefined
    const variant = getJanPriceTestVariant();
    if (variant === 'control') {
      plans = await getDppSkus(['FOXNATION1M-WEB2', 'FOXNATION12M-WEB']);
    } else if (variant === 'variant') {
      plans = await getDppSkus(['FOXNATION1M-WEB7', 'FOXNATION12M-WEB']);
    }
  } finally {
    return plans;
  }
};

//set-default-plan experiment:

//Value of which plan should be expanded in mobile view / preselected in default plan select flow
const getSetDefaultPlanVariant = (plansLength) => {
  // let value = plansLength || 1; //default value - last plan
  let value = 2; //default value - second plan
  const experimentData = experimentsDataObj[SET_DEFAULT_PLAN_KEY];
  if (experimentData && experimentData.value) {
    value = Number(experimentData.value);
  }
  return value;
};

//helper for above experiment to get the right array index in plans list to select
export const getSetDefaultPlanIndex = (plansLength) => {
  exposureEvent(SET_DEFAULT_PLAN_KEY);
  //Make sure index isn't higher than index of last plan in plans list
  const maxIndex = Math.min(getSetDefaultPlanVariant(plansLength), plansLength) - 1;
  //Make sure index is at least 0
  const validIndex = Math.max(maxIndex, 0);
  return validIndex;
};

//default-plan-flow experiment:

export const isDefaultPlanFlow = () => {
  exposureEvent(DEFAULT_PLAN_FLOW_KEY);
  const experimentData = experimentsDataObj[DEFAULT_PLAN_FLOW_KEY];
  return experimentData && experimentData.value && experimentData.value === 'preselected-plan-flow';
};

//need to have a plan selected by default in signup in default plan flow experiment
//this select would also apply to deeplinks to payment page for both signup plans and change plans
export const selectDefaultPlan = (allPlans, activeSubscriptionId) => {
  let planId = '';
  const defaultPlanIndex = getSetDefaultPlanIndex(allPlans.length);
  planId = allPlans[defaultPlanIndex] ? allPlans[defaultPlanIndex].planId : '';

  //for change plans via payment deeplink, need to make sure not selecting current plans
  if (planId === activeSubscriptionId) {
    if (allPlans[defaultPlanIndex - 1]) {
      planId = allPlans[defaultPlanIndex - 1].planId;
    } else {
      planId = allPlans[defaultPlanIndex + 1].planId || '';
    }
  }

  return planId;
};

// TODO: if exposureEvent is the only function needed from this file, should we export it so we can code-split by
// experiment category? ex: PlanExperiments.js, LayoutExperiments.js, EntitlementExperiments.js
export const enableYearlyDiscountExperiment = (plansList) => {
  exposureEvent(PLAN_EXP_FN1YPA47_KEY);

  const checkPlanContainsYearly = (plan) => {
    if (checkIfExperimentIsActive(PLAN_EXP_FN1YPA47_KEY)) {
      return plan.packageID !== 'FOXNATION12M-WEB';
    } else {
      return plan.packageID !== 'FN1YPA47';
    }
  };
  // filter out all experimental packages which are turned off('false') in Amplitude
  return plansList.filter((plan) => checkPlanContainsYearly(plan));
};

export const replaceSKUComboExperiment = (plansList, allPlans) => {
  if (checkIfExperimentIsActive(SKU_PRICE_COMBOS)) {
    if (experimentsDataObj[SKU_PRICE_COMBOS].value === MONTHLY_BAU_YEARLY_LOW) {
      const plansRemovedDefault = plansList.filter((plan) => plan.packageID !== 'FOXNATION12M-WEB');
      const newYearlyExperiment = allPlans.filter((plan) => plan.packageID === 'FN1YPA47');
      return [...plansRemovedDefault, ...newYearlyExperiment];
    } else if (experimentsDataObj[SKU_PRICE_COMBOS].value === MONTHLY_HIGH_YEARLY_LOW) {
      let plansRemovedDefault = plansList.filter((plan) => plan.packageID !== 'FOXNATION1M-WEB2');
      plansRemovedDefault = plansRemovedDefault.filter((plan) => plan.packageID !== 'FOXNATION12M-WEB');
      const newMonthlyExperiment = allPlans.filter((plan) => plan.packageID === 'FOXNATION1M-WEB3');
      const newYearlyExperiment = allPlans.filter((plan) => plan.packageID === 'FN1YPA47');
      if (newYearlyExperiment[0]) {
        newYearlyExperiment[0].dppContentOverride = { mainFeature: 'Five months savings' };
      }
      return [...plansRemovedDefault, ...newMonthlyExperiment, ...newYearlyExperiment];
    } else if (experimentsDataObj[SKU_PRICE_COMBOS].value === MONTHLY_YEARLY_HIGH) {
      let plansRemovedDefault = plansList.filter((plan) => plan.packageID !== 'FOXNATION1M-WEB2');
      plansRemovedDefault = plansRemovedDefault.filter((plan) => plan.packageID !== 'FOXNATION12M-WEB');
      const newMonthlyExperiment = allPlans.filter((plan) => plan.packageID === 'FOXNATION1M-WEB3');
      const newYearlyExperiment = allPlans.filter((plan) => plan.packageID === 'FOXNATION12M-WEB2');
      return [...plansRemovedDefault, ...newMonthlyExperiment, ...newYearlyExperiment];
    }
  }
  return plansList;
};

// two plans funnel from MLP
export const TWO_PLANS_FUNNEL_VARIANTS = {
  control: 'control',
  changeOrder: 'change-order',
  heroPlans: 'hero-plans',
};

export const getSetTwoPlansFunnelVariant = () => {
  return experimentsDataObj[TWO_PLANS_FUNNEL_PLACEMENT]?.value;
};

export const checkIfShowSheerBanner = () => {
  return getSetTwoPlansFunnelVariant() === TWO_PLANS_FUNNEL_VARIANTS.heroPlans;
};

// monthly 30 day free trial experiment
export const MONTHLY_30_DAY_FREE_TRIAL_VARIANTS = {
  control: 'control',
  variant: 'variant',
};

export const getSetMonthly30DayFreeTrialVariant = () => {
  return experimentsDataObj[MONTHLY_30_DAY_FREE_TRIAL]?.value;
};

export const getMonthlyExperimentPlans = (variant, allPlans) => {
  const newPlans = [...allPlans];

  // depending on variant, filter out one of two possible monthly plans (either 7 or 30 day free trial)
  const monthUpgradeIdx = newPlans.findIndex((plan) => plan.packageID === MONTH_UPGRADE_SKU);
  const monthUpgradeVariantIdx = newPlans.findIndex((plan) => plan.packageID === MONTH_UPGRADE_SKU_VARIANT);
  // only filter out plans if:
  // a) if experiment is active (i.e. not undefined)
  // b) both control and variant plans exist in the array
  if (monthUpgradeIdx > -1 && monthUpgradeVariantIdx > -1) {
    if (variant === MONTHLY_30_DAY_FREE_TRIAL_VARIANTS.variant) {
      newPlans.splice(monthUpgradeIdx, 1);
    } else {
      newPlans.splice(monthUpgradeVariantIdx, 1);
    }
  }
  return newPlans;
};

//october-2023-price-test experiment:
export const isOctPriceTest = () => {
  //no exposure event since experiment starts on mlp
  const experimentData = experimentsDataObj[OCT_2023_PRICE_TEST];
  return experimentData && experimentData.value && experimentData.value === 'variant-1';
};

// =======

/**
 *
 *  USER:
 * **/

// User property name constants
const VIDEO_50_PERCENT_WATCHING = '50_percent_watching';
const SUBSCRIPTION_PLAN = 'subscription_plan';
const SUBSCRIPTION_STATE = 'subscription_state';
const PAYMENT_TYPE = 'payment_type';
const MLP_COOKIE_USER_PROPERTY = 'mlp_cmp_var';

// User Property Plans Map
const plansMap = {
  'FOXNATION12M-WEB': 'Annual',
  'FOXNATION1M-WEB2': 'Monthly',
  'FOXNATION1M-WEB4': 'Monthly',
  FN2YPKMR: 'PatriotSilver-2YR',
  FN1YPKMR: 'Patriot-1YR',
  FN3YPKMR: 'PatriotGold-3YR',
  FN3MPK: 'Quarterly',
  FN6MPK: 'Half Year',
};

// User Property Founders Tiers Names
const foundersTiers = [
  'Founders Tier 1 - 1YR',
  'Founders Tier 2 - 2YR',
  'Founders Tier 3 - 3YR',
  'Founders Tier 4 - 3YR',
];

const user = {
  // user_id: '',  //Not sending user ids for now
  device_id: '',
  user_properties: {},
};

const getAmplitudeUser = () => {
  return user;
};

/* 
From Amplitude docs:
If you want the most up-to-date variants for the user, it is recommended that you call fetch()
whenever the user state changes in a meaningful way. For example, if the user logs in and
receives a user ID, or has a user property set which may effect flag or experiment targeting rules.
*/
const setUserProperty = (name, value, fetch = true) => {
  user.user_properties[name] = value;
  if (fetch) fetchAndCollectExperiments(user);
};

/**
 * Call functions to set user properties after Amplitude is loaded
 * see segment integration notes at:
 * https://www.docs.developers.amplitude.com/experiment/sdks/javascript-sdk/#fetch
 */
const setInitUserAttributes = () => {
  // user.user_id = getAnalyticsUserId(); //Not sending user ids for now

  const deviceId = getAnonymousId();
  user.device_id = deviceId;

  user.user_properties = {};

  setMlpCookieProperty();
  set50PercentWatchingProperty(false);
};

/**
 * Helper to get plan name for user properties
 * @returns {*|Array}
 */
const getPlanName = (activeSubscriptionId, serviceName, tier, isFounders) => {
  if (isFounders && tier) {
    return foundersTiers[tier - 1];
  }
  // If the name is not in the plansMap, likely a new plan or a v2 plan.
  // If so, use the serviceName until a mapped name is decided.
  return plansMap[activeSubscriptionId] ?? serviceName;
};

/**
 * Helper to get user property value for 'subscription_state'
 */
const getSubState = (value, isLogOut = false) => {
  if (value.length === 0) return null;
  const status = value.toLowerCase();
  if (status.includes('existing') || status.includes('new')) {
    if (isLogOut) {
      return 'paid_signed_out';
    }
    return 'paid';
  }
  if (status.includes('pending cancel')) {
    return 'pending_cancel';
  }
  if (status.includes('trialist')) {
    return 'trialist';
  }
  if (status.includes('pause')) {
    return 'pause';
  }
  if (status.includes('churned involuntarily')) {
    return 'churned_involuntary';
  }
  if (status.includes('churned voluntary')) {
    return 'churned_voluntary';
  }
  return null;
};

/**
 * Sets user property for 'subscription_state'
 */
export const setSubscriptionStateProperty = (status, isLogOut = false) => {
  const value = getSubState(status, isLogOut);
  if (value) {
    setUserProperty(SUBSCRIPTION_STATE, value);
  }
};

/**
 * Sets user property for 'payment_state'
 */
export const setPaymentTypeProperty = (paymentType = '') => {
  let value = paymentType.toLowerCase();
  if (value.includes('card')) {
    value = 'card';
  }
  if (value.includes('applepay')) {
    value = 'applepay';
  }
  if (value.includes('paypal')) {
    value = 'paypal';
  }
  if (value) {
    setUserProperty(PAYMENT_TYPE, value);
  }
};

/**
 * Sets user property for 'subscription_plan'
 */
export const setSubscriptionPlanProperty = ({ serviceID, serviceName, tier, startDate }) => {
  const isFounders = isFoundingMember(serviceID, startDate);
  const planName = getPlanName(serviceID, serviceName, tier, isFounders);
  if (planName) setUserProperty(SUBSCRIPTION_PLAN, planName);
};

/**
 * Sets user property for '50_percent_watching'
 */
const set50PercentWatchingProperty = (fetch) => {
  const hasWatched50Percent = JSON.parse(localStorage.getItem('video50PercentWatching'));
  setUserProperty(VIDEO_50_PERCENT_WATCHING, !!hasWatched50Percent, fetch);
};

/**
 * Sets user property for '50_percent_watching'
 */
const setMlpCookieProperty = () => {
  if (cookies.get(MLP_COOKIE)) {
    setUserProperty(MLP_COOKIE_USER_PROPERTY, true, false);
  }
};
